import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CollectionSharingService } from '@/services/api';

interface SharedCollection {
  id: string;
  name: string;
  description?: string;
  permission: 'read' | 'write' | 'admin';
  owner: {
    id: string;
    username: string;
    email?: string;
  };
  shared_at: string;
}

interface Invitation {
  id: string;
  collection_id: string;
  collection_name: string;
  inviter_name: string;
  permission: 'read' | 'write' | 'admin';
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expires_at: string;
  created_at: string;
}

interface CollectionSharingState {
  sharedCollections: SharedCollection[];
  invitations: Invitation[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchSharedCollections: () => Promise<void>;
  fetchInvitations: (status?: string) => Promise<void>;
  acceptInvitation: (invitationToken: string) => Promise<void>;
  rejectInvitation: (invitationToken: string) => Promise<void>;
  inviteUser: (collectionId: string, email?: string, username?: string, permission?: 'read' | 'write' | 'admin') => Promise<void>;
  leaveSharedCollection: (collectionId: string) => Promise<void>;
  clearError: () => void;
}

export const useCollectionSharingStore = create<CollectionSharingState>()(
  devtools(
    (set, get) => ({
      sharedCollections: [],
      invitations: [],
      isLoading: false,
      error: null,

      fetchSharedCollections: async () => {
        set({ isLoading: true, error: null });
        try {
          const collections = await CollectionSharingService.listSharedCollections();
          set({ 
            sharedCollections: Array.isArray(collections) ? collections.map((col: unknown) => {
              const collection: Record<string, unknown> = (col && typeof col === 'object') ? (col as Record<string, unknown>) : {};
              const ownerUnknown = collection.owner;
              const owner: Record<string, unknown> | undefined = (ownerUnknown && typeof ownerUnknown === 'object') ? (ownerUnknown as Record<string, unknown>) : undefined;
              const getString = (val: unknown): string => typeof val === 'string' ? val : ''
              const getPermission = (val: unknown): 'read' | 'write' | 'admin' => (typeof val === 'string' && ['read','write','admin'].includes(val)) ? (val as 'read' | 'write' | 'admin') : 'read'
              return {
                id: getString(collection.id),
                name: getString(collection.name),
                description: typeof collection.description === 'string' ? collection.description : undefined,
                permission: getPermission(collection.permission),
                owner: {
                  id: getString(owner?.id),
                  username: getString(owner?.username),
                  email: typeof owner?.email === 'string' ? owner.email : undefined
                },
                shared_at: getString(collection.shared_at) || new Date().toISOString()
              };
            }) : [],
            isLoading: false 
          });
        } catch (error: unknown) {
          console.error('Failed to fetch shared collections:', error);
          const errorObj: Record<string, unknown> = (error && typeof error === 'object') ? (error as Record<string, unknown>) : {};
          const body = (errorObj && typeof errorObj.body === 'object') ? (errorObj.body as Record<string, unknown>) : undefined;
          set({ 
            error: (typeof body?.message === 'string' ? body.message : (typeof errorObj?.message === 'string' ? errorObj.message : 'Failed to fetch shared collections')),
            isLoading: false 
          });
        }
      },

      fetchInvitations: async (status = 'pending') => {
        set({ isLoading: true, error: null });
        try {
          const invitations = await CollectionSharingService.listUserInvitations({ 
            status: (['pending','accepted','rejected','expired'].includes(status) ? (status as 'pending' | 'accepted' | 'rejected' | 'expired') : 'pending')
          });
          set({ 
            invitations: Array.isArray(invitations) ? invitations.map((inv: unknown) => {
              const invitation: Record<string, unknown> = (inv && typeof inv === 'object') ? (inv as Record<string, unknown>) : {};
              const getString = (val: unknown): string => typeof val === 'string' ? val : ''
              const isPermission = (val: unknown): val is 'read' | 'write' | 'admin' => typeof val === 'string' && ['read','write','admin'].includes(val)
              const isStatus = (val: unknown): val is 'pending' | 'accepted' | 'rejected' | 'expired' => typeof val === 'string' && ['pending','accepted','rejected','expired'].includes(val)
              return {
                id: getString(invitation.id),
                collection_id: getString(invitation.collection_id),
                collection_name: getString(invitation.collection_name),
                inviter_name: getString(invitation.inviter_name),
                permission: isPermission(invitation.permission) ? invitation.permission : 'read',
                status: isStatus(invitation.status) ? invitation.status : 'pending',
                expires_at: getString(invitation.expires_at) || new Date().toISOString(),
                created_at: getString(invitation.created_at) || new Date().toISOString()
              };
            }) : [],
            isLoading: false 
          });
        } catch (error: unknown) {
          console.error('Failed to fetch invitations:', error);
          const errorObj: Record<string, unknown> = (error && typeof error === 'object') ? (error as Record<string, unknown>) : {};
          const body = (errorObj && typeof errorObj.body === 'object') ? (errorObj.body as Record<string, unknown>) : undefined;
          set({ 
            error: (typeof body?.message === 'string' ? body.message : (typeof errorObj?.message === 'string' ? errorObj.message : 'Failed to fetch invitations')),
            isLoading: false 
          });
        }
      },

      acceptInvitation: async (invitationToken: string) => {
        set({ isLoading: true, error: null });
        try {
          await CollectionSharingService.acceptCollectionInvitation({
            requestBody: { invitation_token: invitationToken }
          });
          
          // Remove accepted invitation from the list
          set((state) => ({
            invitations: state.invitations.filter(inv => inv.id !== invitationToken),
            isLoading: false
          }));
          
          // Refresh shared collections
          get().fetchSharedCollections();
        } catch (error: unknown) {
          console.error('Failed to accept invitation:', error);
          const errorObj: Record<string, unknown> = (error && typeof error === 'object') ? (error as Record<string, unknown>) : {};
          const body = (errorObj && typeof errorObj.body === 'object') ? (errorObj.body as Record<string, unknown>) : undefined;
          const message = typeof body?.message === 'string' ? body.message : (typeof errorObj?.message === 'string' ? errorObj.message : 'Failed to accept invitation')
          set({ 
            error: message,
            isLoading: false 
          });
        }
      },

      rejectInvitation: async (invitationToken: string) => {
        set({ isLoading: true, error: null });
        try {
          await CollectionSharingService.rejectCollectionInvitation({
            invitationToken,
            requestBody: {}
          });
          
          // Remove rejected invitation from the list
          set((state) => ({
            invitations: state.invitations.filter(inv => inv.id !== invitationToken),
            isLoading: false
          }));
        } catch (error: unknown) {
          console.error('Failed to reject invitation:', error);
          const errorObj: Record<string, unknown> = (error && typeof error === 'object') ? (error as Record<string, unknown>) : {};
          const body = (errorObj && typeof errorObj.body === 'object') ? (errorObj.body as Record<string, unknown>) : undefined;
          const message = typeof body?.message === 'string' ? body.message : (typeof errorObj?.message === 'string' ? errorObj.message : 'Failed to reject invitation')
          set({ 
            error: message,
            isLoading: false 
          });
        }
      },

      inviteUser: async (collectionId: string, email?: string, username?: string, permission: 'read' | 'write' | 'admin' = 'read') => {
        set({ isLoading: true, error: null });
        try {
          const requestBody: { 
            permission: 'read' | 'write' | 'admin';
            email?: string;
            username?: string;
          } = { permission };
          if (email) requestBody.email = email;
          if (username) requestBody.username = username;

          await CollectionSharingService.inviteUserToCollection({
            collectionId,
            requestBody
          });
          
          set({ isLoading: false });
        } catch (error: unknown) {
          console.error('Failed to invite user:', error);
          const errorObj: Record<string, unknown> = (error && typeof error === 'object') ? (error as Record<string, unknown>) : {};
          const body = (errorObj && typeof errorObj.body === 'object') ? (errorObj.body as Record<string, unknown>) : undefined;
          const message = typeof body?.message === 'string' ? body.message : (typeof errorObj?.message === 'string' ? errorObj.message : 'Failed to invite user')
          set({ 
            error: message,
            isLoading: false 
          });
          throw error; // Re-throw so calling component can handle it
        }
      },

      leaveSharedCollection: async (collectionId: string) => {
        set({ isLoading: true, error: null });
        try {
          await CollectionSharingService.leaveSharedCollection({ collectionId });
          
          // Remove the collection from shared collections
          set((state) => ({
            sharedCollections: state.sharedCollections.filter(col => col.id !== collectionId),
            isLoading: false
          }));
        } catch (error: unknown) {
          console.error('Failed to leave collection:', error);
          const errorObj = (error && typeof error === 'object') ? error as Record<string, unknown> : {};
          const body = (errorObj && typeof errorObj.body === 'object') ? (errorObj.body as Record<string, unknown>) : undefined;
          const message = typeof body?.message === 'string' ? body.message : (typeof errorObj?.message === 'string' ? errorObj.message : 'Failed to leave collection')
          set({ 
            error: message,
            isLoading: false 
          });
          throw error; // Re-throw so calling component can handle it
        }
      },

      clearError: () => { set({ error: null }) }
    }),
    {
      name: 'collection-sharing-store'
    }
  )
);